/**
 * FinalConclusionScene — Phaser Scene displayed after completing all 4 worlds.
 *
 * Displays:
 * - Full-screen conclusion image (conclusionImageKey)
 * - Congratulatory narrative text (pt-PT, age-appropriate for 4-8 years)
 * - "Jogar Novamente" button that resets all progress and returns to MapSelectScene
 *
 * On reset: clears all localStorage keys starting with "awe_" prefix
 * (awe_progress_*, awe_collectibles_*, awe_completed_worlds).
 *
 * Requirements: 6.1, 6.2, 6.3, 6.4, 6.5
 */

import Phaser from 'phaser';

// ─── Interfaces ──────────────────────────────────────────────────────────────

export interface FinalConclusionSceneData {
  conclusionImageKey: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

/** Congratulatory narrative text in pt-PT, age-appropriate for 4-8 years */
const CONGRATULATIONS_TEXT =
  'Parabéns, aventureiro! Exploraste todos os mundos e completaste a tua grande aventura! És um verdadeiro herói!';

/** Button label */
const BUTTON_LABEL = 'Jogar Novamente';

/** LocalStorage key prefix to clear on reset */
const STORAGE_PREFIX = 'awe_';

/** Button styling */
const BUTTON_WIDTH = 280;
const BUTTON_HEIGHT = 56;
const BUTTON_CORNER_RADIUS = 14;
const BUTTON_COLOR = 0x4caf50;
const BUTTON_HOVER_COLOR = 0x66bb6a;

// ─── FinalConclusionScene Class ──────────────────────────────────────────────

export class FinalConclusionScene extends Phaser.Scene {
  private conclusionImageKey: string = 'conclusao-final';

  constructor() {
    super({ key: 'FinalConclusionScene' });
  }

  init(data?: FinalConclusionSceneData): void {
    if (data?.conclusionImageKey) {
      this.conclusionImageKey = data.conclusionImageKey;
    }
  }

  create(): void {
    const { width, height } = this.scale;
    const centerX = width / 2;
    const centerY = height / 2;

    // ─── Full-screen conclusion image ────────────────────────────────────
    if (this.textures.exists(this.conclusionImageKey)) {
      const bgImage = this.add.image(centerX, centerY, this.conclusionImageKey);
      bgImage.setDisplaySize(width, height);
      bgImage.setDepth(0);
    } else {
      // Fallback: gradient-like colored background if image not available
      const bg = this.add.rectangle(centerX, centerY, width, height, 0x1a0533, 1);
      bg.setDepth(0);
    }

    // ─── Semi-transparent overlay for text readability ───────────────────
    const overlay = this.add.rectangle(centerX, centerY, width, height, 0x000000, 0.4);
    overlay.setDepth(1);

    // ─── Congratulatory narrative text ───────────────────────────────────
    const narrativeText = this.add.text(centerX, centerY - 60, CONGRATULATIONS_TEXT, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '28px',
      color: '#ffffff',
      fontStyle: 'bold',
      wordWrap: { width: width * 0.75 },
      align: 'center',
      stroke: '#000000',
      strokeThickness: 3,
    });
    narrativeText.setOrigin(0.5, 0.5);
    narrativeText.setDepth(2);

    // Gentle fade-in animation for the text
    narrativeText.setAlpha(0);
    this.tweens.add({
      targets: narrativeText,
      alpha: 1,
      duration: 800,
      ease: 'Power2',
      delay: 300,
    });

    // ─── "Jogar Novamente" button ────────────────────────────────────────
    const buttonY = centerY + 120;

    // Button background
    const buttonBg = this.add.graphics();
    buttonBg.fillStyle(BUTTON_COLOR, 1);
    buttonBg.fillRoundedRect(
      centerX - BUTTON_WIDTH / 2,
      buttonY - BUTTON_HEIGHT / 2,
      BUTTON_WIDTH,
      BUTTON_HEIGHT,
      BUTTON_CORNER_RADIUS
    );
    buttonBg.setDepth(2);

    // Button text
    const buttonText = this.add.text(centerX, buttonY, BUTTON_LABEL, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '22px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    buttonText.setOrigin(0.5, 0.5);
    buttonText.setDepth(3);

    // Interactive hit area for button
    const buttonHitArea = this.add
      .rectangle(centerX, buttonY, BUTTON_WIDTH, BUTTON_HEIGHT, 0xffffff, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(3);

    // Button fade-in animation (appears after text)
    buttonBg.setAlpha(0);
    buttonText.setAlpha(0);
    buttonHitArea.setAlpha(0);
    this.tweens.add({
      targets: [buttonBg, buttonText, buttonHitArea],
      alpha: 1,
      duration: 500,
      ease: 'Power2',
      delay: 1200,
    });

    // ─── Button interactions ─────────────────────────────────────────────
    buttonHitArea.on('pointerover', () => {
      buttonBg.clear();
      buttonBg.fillStyle(BUTTON_HOVER_COLOR, 1);
      buttonBg.fillRoundedRect(
        centerX - BUTTON_WIDTH / 2,
        buttonY - BUTTON_HEIGHT / 2,
        BUTTON_WIDTH,
        BUTTON_HEIGHT,
        BUTTON_CORNER_RADIUS
      );
    });

    buttonHitArea.on('pointerout', () => {
      buttonBg.clear();
      buttonBg.fillStyle(BUTTON_COLOR, 1);
      buttonBg.fillRoundedRect(
        centerX - BUTTON_WIDTH / 2,
        buttonY - BUTTON_HEIGHT / 2,
        BUTTON_WIDTH,
        BUTTON_HEIGHT,
        BUTTON_CORNER_RADIUS
      );
    });

    buttonHitArea.on('pointerdown', () => {
      this.resetAllProgress();
      this.scene.start('MapSelectScene');
    });
  }

  /**
   * Resets all game progress by clearing all localStorage keys with the "awe_" prefix.
   * This includes: awe_progress_*, awe_collectibles_*, awe_completed_worlds.
   */
  private resetAllProgress(): void {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(STORAGE_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      for (const key of keysToRemove) {
        localStorage.removeItem(key);
      }
    } catch {
      // Graceful degradation: if localStorage is unavailable, continue without clearing
    }
  }
}
